import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Controller, { inject as controller } from '@ember/controller';
import { task } from 'ember-concurrency';
import fade from 'ember-animated/transitions/fade';
import RouterService from '@ember/routing/router-service';
import { isEmpty } from '@ember/utils';
import FormValidations from 'cio/utils/form-validations';
import isValidEmail from 'cio/utils/validators/email';
import AlertsService from 'cio/services/alerts';
import SignupService from 'cio/services/signup';
import SignupController from 'cio/pods/signup/controller';
import AnalyticsService from 'cio/services/analytics';

// Same regex as backend: https://github.com/customerio/services/blob/d1630479544d2956fd8dfd77fc1c95217d7bcc03/ui_api/accounts/signup.go#L66
const urlRegex = /\S+\.\w{2,}/;

const Validations = {
  firstName(value: string) {
    if (isEmpty(value?.trim())) {
      return "First name can't be blank";
    }

    if (urlRegex.test(value)) {
      return 'First name cannot contain a URL';
    }

    return true;
  },

  lastName(value: string) {
    if (urlRegex.test(value)) {
      return 'Last name cannot contain a URL';
    }

    return true;
  },

  email(value: string) {
    if (!isValidEmail(value)) {
      return 'Please enter a valid email address';
    }

    return true;
  },
};

export default class SignupIndexController extends Controller {
  // These ideally should be on the signup controller, but if we do that, the
  // query params leak across all child routes.
  queryParams = ['email', 'first', 'last'];

  email: string | null = null;
  first: string | null = null;
  last: string | null = null;

  validations!: FormValidations;

  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service signup!: SignupService;
  @service router!: RouterService;

  @controller('signup') signupController!: SignupController;

  _emailInputTouched = false;

  fade = fade;

  constructor() {
    super(...arguments);
    this.set(
      'validations',
      FormValidations.create({ validations: Validations, context: this, modelKey: 'signupController.formValues' }),
    );
  }

  get submitDisabled() {
    return this.validations.hasErrors || this._submitTask.isRunning;
  }

  _submitTask = task(async (ev: MouseEvent) => {
    ev.preventDefault();

    try {
      await this.validations.validateModel();
    } catch (e) {
      // `validateModel` already found and set validation errors
      // return early to avoid executing real submit logic
      return;
    }

    try {
      let signupInitData = await this.signup.initSignup(this.signupController.formValues.email);
      // signup.initData resolves with "false" if email is already taken:
      if (!signupInitData) {
        return this.router.transitionTo('signup.check-email');
      }

      this.signupController.setFormValue('companyName', signupInitData.companyName);
      this.signupController.setFormValue('role', signupInitData.role);

      this.analytics.track('signup', 'name_and_email_submitted', {
        signupType: this.signupController.model.signupType.name,
      });
      return this.signupController.goToNextStep();
    } catch (err) {
      this.alerts.error('signup.error', 'Problem signing up. Try again later.');
    }

    return;
  });

  setDefaults() {
    this.setProperties({
      _emailInputTouched: false,
    });
  }

  @action
  onEmailBlur() {
    this.set('_emailInputTouched', this._emailInputTouched || Boolean(this.signupController.formValues.email));
  }

  @action
  onFormValueChange(field: keyof SignupController['formValues'], value: string) {
    this.signupController.setFormValue(field, value);
    this.validations.validateAttribute(field);
  }
}
